import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/Sales/Advertising Logo_Primary.jpg';

const AdvertisingLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: 'Home', path: '/advertising', type: 'route' },
    { label: 'Artwork Specs', path: '/advertising/artwork-specs', type: 'route' },
    { label: 'Testimonials', path: '#testimonials', type: 'scroll' },
    { label: 'Contact Us', path: '#contact', type: 'scroll' }
  ];

  const handleNavigation = (item) => {
    if (item.type === 'scroll') {
      // If we're not on the advertising page, navigate there first
      if (location.pathname !== '/advertising') {
        navigate('/advertising');
        setTimeout(() => {
          const element = document.getElementById(item.path.substring(1));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(item.path.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(item.path);
    }
    setMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    if (path === '/advertising') {
      return location.pathname === '/advertising';
    }
    return location.pathname === path;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            {/* Logo */}
            <Box
              component="img"
              src={logo}
              alt="Austnews Advertising Logo"
              sx={{
                height: { xs: 40, md: 50 },
                cursor: 'pointer',
                mr: 4
              }}
              onClick={() => navigate('/advertising')}
            />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => handleNavigation(item)}
                    sx={{
                      color: isActivePath(item.path) ? '#247AA8' : '#333132',
                      fontWeight: isActivePath(item.path) ? 600 : 400,
                      borderBottom: isActivePath(item.path) ? '2px solid #247AA8' : 'none',
                      borderRadius: 0,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#247AA8'
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Mobile Menu Icon */}
            {isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  size="large"
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{ color: '#333132' }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 250, pt: 2 }}>
          <List>
            {navItems.map((item) => (
              <ListItem
                button
                key={item.path}
                onClick={() => handleNavigation(item)}
                sx={{
                  backgroundColor: isActivePath(item.path) ? 'rgba(36, 122, 168, 0.1)' : 'transparent',
                  borderLeft: isActivePath(item.path) ? '4px solid #247AA8' : 'none'
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    color: isActivePath(item.path) ? '#247AA8' : '#333132',
                    fontWeight: isActivePath(item.path) ? 600 : 400
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
    </Box>
  );
};

export default AdvertisingLayout;

